<template>
    <button type="button" class="v-btn" :class="buttonClass" :disabled="disabled" ref="elementRef">
        <slot></slot>
    </button>
</template>

<script setup lang="ts">
    import useRippleEffect from "@/components/composables/rippleEffect";
    import { computed } from "vue";

    type ButtonType = "primary" | "secondary" | "success" | "info" | "warning" | "danger";

    const props = defineProps<{
        severity?: ButtonType;
        disabled?: boolean;
        ripple?: boolean;
    }>();
    const shouldRipple = computed(() => (props.ripple === undefined || props.ripple === true) && !props.disabled);

    const buttonClass = computed(() => {
        return {
            primary: props.severity === "primary",
            secondary: props.severity === "secondary",
            success: props.severity === "success",
            info: props.severity === "info",
            warning: props.severity === "warning",
            danger: props.severity === "danger",
        };
    });

    const { elementRef } = useRippleEffect({
        disabled: shouldRipple,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    $tBtnBorderWidth: 2px;

    .v-btn {
        border-radius: 0.375em;
        font-size: 14px;
        font-weight: 300;
        text-align: center;
        white-space: nowrap;
        box-sizing: border-box;
        display: inline-block;
        cursor: pointer;
        pointer-events: all;
        transition:
            background-color 0.5s variables.$easeOutExpo,
            border-color 0.5s variables.$easeOutExpo;
        line-height: 1em;
        padding: 0.75em 1.25em;
        border: none;

        color: colors.$fontColor;
        background-color: colors.$white;
        border: $tBtnBorderWidth solid colors.$white;

        padding: 0 calc(1.25em - $tBtnBorderWidth);
        line-height: calc(2.5em - 2 * $tBtnBorderWidth);

        @include mixins.hover-and-active() {
            background-color: colors.$light-gray-100;
            border-color: colors.$light-gray-100;
        }

        &.primary {
            background-color: colors.$green-500;
            border-color: colors.$green-500;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$green-400;
                border-color: colors.$green-400;
            }
        }

        &.secondary {
            background-color: colors.$light-gray-100;
            border-color: colors.$light-gray-100;
            color: colors.$fontColor;

            @include mixins.hover-and-active() {
                background-color: colors.$light-gray-300;
                border-color: colors.$light-gray-300;
            }
        }

        &.success {
            background-color: colors.$green-500;
            border-color: colors.$green-500;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$green-400;
                border-color: colors.$green-400;
            }
        }

        &.warning {
            background-color: colors.$orange-500;
            border-color: colors.$orange-500;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$orange-700;
                border-color: colors.$orange-700;
            }
        }

        // todo: fix color pallette
        &.danger {
            background-color: colors.$red-600;
            border-color: colors.$red-600;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$red-900;
                border-color: colors.$red-900;
            }
        }

        // todo: add color pallette
        &.info {
            background-color: lightskyblue;
            border-color: skyblue;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: skyblue;
                border-color: skyblue;
            }
        }

        &.pink {
            background-color: colors.$pink-500;
            border-color: colors.$pink-500;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$pink-700;
                border-color: colors.$pink-700;
            }
        }

        &.contrast {
            background-color: colors.$dark-gray-500;
            border-color: colors.$dark-gray-500;
            color: colors.$white;

            @include mixins.hover-and-active() {
                background-color: colors.$dark-gray-700;
                border-color: colors.$dark-gray-700;
            }
        }

        &.disabled,
        &[disabled] {
            background-color: colors.$lightGrey !important;
            color: colors.$white !important;
            cursor: default !important;
            border-color: colors.$lightGrey !important;
        }

        &.rounded {
            border-radius: 1.25em !important;
        }

        &.outlined {
            background-color: colors.$white;
            border-width: $tBtnBorderWidth;
            border-style: solid;

            @include mixins.hover-and-active() {
                background-color: colors.$light-gray-100;
            }

            &.primary {
                color: colors.$green-900;
            }

            &.secondary {
                // color: colors.$green-900;
                border-color: colors.$light-gray-200;
            }

            &.success {
                color: colors.$green-900;
            }

            &.warning {
                color: colors.$orange-700;
            }

            &.danger {
                color: colors.$red-600;
            }

            // todo: add color pallette
            &.info {
                color: skyblue;
            }

            &.pink {
                color: colors.$pink-700;
            }

            &.contrast {
                color: colors.$white;
                background-color: colors.$dark-gray-500;
                border-color: colors.$white;

                @include mixins.hover-and-active() {
                    background-color: colors.$dark-gray-700;
                }
            }
        }

        &.loading {
            position: relative;
            cursor: default !important;

            &:before {
                content: " ";
                background-image: url(/assets/img/app/loading_white.svg);
                width: 1em;
                display: inline-block;
                vertical-align: middle;
                height: 1em;
                margin-right: 0.5em;
                background-size: contain;
                margin-bottom: 0.25em;
            }
        }
    }
</style>
